import {useMemo} from 'react';
import dot from 'dot-object';
import {useAuth} from '../auth/use-auth';
import {useSettings} from '@ui/settings/use-settings';
import {User} from '@ui/types/user';
import {MenuConfig, MenuItemConfig} from '@common/menus/menu-config';
import {Subscription} from '@common/billing/subscription';

export function useCustomMenu(menuOrPosition?: string | MenuConfig) {
  const settings = useSettings();
  const {user, hasPermission} = useAuth();

  return useMemo(() => {
    if (!menuOrPosition) {
      return null;
    }

    const sourceMenu =
      typeof menuOrPosition === 'string'
        ? settings.menus?.find(menu =>
            menu.positions?.includes(menuOrPosition),
          )
        : menuOrPosition;

    if (!sourceMenu) {
      return null;
    }

    const items = sourceMenu.items
      .filter(item => itemMatchesCurrentUser(item, user, settings, hasPermission))
      .map(item => {
        const action =
          user?.id && item.action
            ? item.action.replace(/{currentUser}/g, `${user.id}`)
            : item.action;

        return {
          ...item,
          action,
        };
      });

    return {
      ...sourceMenu,
      items,
    };
  }, [hasPermission, settings, menuOrPosition, user]);
}

function itemMatchesCurrentUser(
  item: MenuItemConfig,
  user: User | null,
  settings: Record<string, any>,
  hasPermission: (permission: string) => boolean,
): boolean {
  if (!item.action) {
    return false;
  }

  if (!subscriptionStatusMatches(item, user)) {
    return false;
  }

  if (
    item.settings &&
    !Object.entries(item.settings).every(([key, value]) => dot.pick(key, settings) == value)
  ) {
    return false;
  }

  if (item.roles?.length) {
    const hasAnyRole = item.roles.some(roleItem => {
      const roleId = typeof roleItem === 'number' ? roleItem : roleItem?.id;
      return roleId != null ? !!user?.roles?.some(role => role.id === roleId) : false;
    });

    if (!hasAnyRole) {
      return false;
    }
  }

  if (item.permissions?.length) {
    const hasAnyPermission = item.permissions.some(permissionItem =>
      permissionMatches(permissionItem, user, hasPermission),
    );

    if (!hasAnyPermission) {
      return false;
    }
  }

  return true;
}

function permissionMatches(
  permissionItem: number | string | {id?: number; name?: string},
  user: User | null,
  hasPermission: (permission: string) => boolean,
): boolean {
  if (typeof permissionItem === 'string') {
    return hasPermission(permissionItem);
  }

  if (typeof permissionItem === 'number') {
    return !!user?.permissions?.some(permission => permission.id === permissionItem);
  }

  if (permissionItem?.name) {
    return hasPermission(permissionItem.name);
  }

  if (permissionItem?.id != null) {
    return !!user?.permissions?.some(permission => permission.id === permissionItem.id);
  }

  return false;
}

function subscriptionStatusMatches(
  item: MenuItemConfig,
  user?: User | null,
): boolean {
  if (!item.subscriptionStatus) {
    return true;
  }

  const hasActiveSubscription = !!user?.subscriptions?.find(sub => sub.active);

  if (item.subscriptionStatus === 'subscribed') {
    return hasActiveSubscription;
  }

  if (item.subscriptionStatus === 'user-subscribed') {
    return hasRole(user?.subscriptions, 'user');
  }
  if (item.subscriptionStatus === 'user-unsubscribed') {
    return !hasRole(user?.subscriptions, 'user');
  }

  if (item.subscriptionStatus === 'artist-subscribed') {
    return hasRole(user?.subscriptions, 'artist');
  }
  if (item.subscriptionStatus === 'artist-unsubscribed') {
    return !hasRole(user?.subscriptions, 'artist');
  }

  if (item.subscriptionStatus === 'creator-subscribed') {
    return hasRole(user?.subscriptions, 'creator');
  }
  if (item.subscriptionStatus === 'creator-unsubscribed') {
    return !hasRole(user?.subscriptions, 'creator');
  }

  if (item.subscriptionStatus === 'label-subscribed') {
    return hasRole(user?.subscriptions, 'label');
  }
  if (item.subscriptionStatus === 'label-unsubscribed') {
    return !hasRole(user?.subscriptions, 'label');
  }

  if (item.subscriptionStatus === 'distributor-subscribed') {
    return hasRole(user?.subscriptions, 'distributor');
  }
  if (item.subscriptionStatus === 'distributor-unsubscribed') {
    return !hasRole(user?.subscriptions, 'distributor');
  }

  return !hasActiveSubscription;
}

function hasRole(
  subscriptions: Omit<Subscription, 'user'>[] | undefined,
  roleName: string,
) {
  if (!subscriptions) return false;

  return subscriptions.some(subscription =>
    subscription?.product?.roles.some((role: any) => role?.name === roleName),
  );
}
